/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useRef } from 'react';
import { Card, Form, Button, Modal, InputGroup } from 'react-bootstrap';
import { PencilIcon, UserGroupIcon } from '@heroicons/react/solid';
import { ChoosePhotoWidget } from '@vlinder-web/choose-photo-widget-react';

export interface INewHolderGroupModel {
  showModal?: boolean;
  createModalLabel?: string;
  nameLabel?: string;
  logoLabel?: string;
  descriptionLabel?: string;
  submitButtonText?: string;
  signupButtonStyle?: any;
  groupNamePlaceholder?: string;
  onAcknowledgement?(
    email: string,
    password: string,
    confirmPassword: string,
    tnc: boolean,
    profileImg?: string
  ): void;
  onDeny?(): void;
}

export const NewHolderGroupModal = ({
  showModal,
  createModalLabel,
  nameLabel,
  logoLabel,
  descriptionLabel,
  submitButtonText,
  signupButtonStyle,
  onDeny,
  onAcknowledgement,
  groupNamePlaceholder,
}: INewHolderGroupModel) => {
  const [show, setShow] = useState(showModal);
  const [profileImg, setProfileImg] = useState<any>(null);

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);

  const handleClose = () => {
    if (onDeny) {
      onDeny();
    }
    setShow(false);
  };

  const handlePhotoChosen = (file: any) => {
    setProfileImg(file?.base64);
  };

  const _onFormSubmit = (event: any) => {
    if (event) {
      event.preventDefault();
      if (onAcknowledgement) {
        onAcknowledgement(
          nameRef?.current?.value,
          descriptionRef?.current?.value,
          profileImg
        );
      }
      // 👇️ clear all input values in the form
      event.target.reset();
    }
    setShow(false);
  };

  return (
    <Modal centered show={show} onHide={handleClose}>
      <Modal.Header className="border-0">
        <Button variant="close" aria-label="Close" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="p-0">
        <Card border="light" className="px-0 px-md-4 py-0 border-0">
          <Card.Header className="text-center text-md-center border-0 mb-0 mt-md-0">
            <h4>{createModalLabel}</h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={_onFormSubmit}>
              <Form.Group id="name" className="mb-4">
                <Form.Label>{nameLabel}</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="text-gray-500">
                    <UserGroupIcon className="icon icon-xs" />
                  </InputGroup.Text>
                  <Form.Control
                    ref={nameRef}
                    autoFocus
                    required
                    type="text"
                    placeholder={groupNamePlaceholder}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group>
                <ChoosePhotoWidget
                  title={logoLabel}
                  multiple={false}
                  onDone={handlePhotoChosen}
                />
              </Form.Group>
              <Form.Group id="description" className="mb-4">
                <Form.Label>{descriptionLabel}</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="text-gray-500">
                    <PencilIcon className="icon icon-xs" />
                  </InputGroup.Text>
                  <Form.Control as="textarea" rows="3" ref={descriptionRef} />
                </InputGroup>
              </Form.Group>
              <div className="d-grid">
                <Button
                  variant="gray-800"
                  type="submit"
                  style={signupButtonStyle}
                >
                  {submitButtonText}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

NewHolderGroupModal.defaultProps = {
  showModal: false,
  createModalLabel: 'Add New Group',
  logoLabel: 'Group Logo',
  nameLabel: 'Group Name',
  descriptionLabel: 'Group Description',
  submitButtonText: 'Add New Group',
  groupNamePlaceholder: 'Enter Group Name',
};
